import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useEffect, useState } from "react";
import Accordion from "@/components/campaignBuilder/Accordion";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { formatDisplayDate } from "@/lib/reminders";
import { defaultScheduleDate } from "@/utils/scheduleDate-helper";
import { isoToZonedSlot, resolveCampaignTimeZone } from "@/utils/timezone-helper";
import { ConditionalPanel, RadioOption, StyledInput } from "./shared";

interface Props {
  isOpen: boolean;
  onToggle: () => void;
}

/** Below this many days from the send date, the sender is asked to confirm (story AC). */
const SHORT_EXPIRY_DAYS = 7;
const DAY_MS = 86_400_000;

/** Purchased credits expire 12 months from purchase, so a campaign can't close later than that —
 *  the credits funding it would lapse before it did. Mirrored by the server's capCustomExpiryDate. */
const EXPIRY_MAX_MONTHS = 12;

/** `YYYY-MM-DD` + whole calendar months, as another `YYYY-MM-DD`. UTC math so no zone/DST drift
 *  moves the day — mirrors the server's addMonthsToDateOnly. */
function addMonthsToDateStr(dateStr: string, months: number): string {
  const [y, m, d] = dateStr.split("-").map((n) => Number.parseInt(n, 10));
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + months, d ?? 1)).toISOString().slice(0, 10);
}

/** Whole days from the send date to the chosen expiry; null when either end is unusable. */
function expiryWindowDays(sendAnchor: string, expiry: string): number | null {
  if (!sendAnchor || !expiry) return null;
  const a = new Date(sendAnchor);
  const e = new Date(expiry);
  if (Number.isNaN(a.getTime()) || Number.isNaN(e.getTime())) return null;
  return Math.ceil((e.getTime() - a.getTime()) / DAY_MS);
}

export default function Expiration({ isOpen, onToggle }: Props) {
  const {
    expiryMode,
    setExpiryMode,
    customExpiryDate,
    setCustomExpiryDate,
    scheduleMode,
    scheduleDate,
    scheduleTz,
    notificationMode,
    activationMode,
    activationDate,
    activationTz,
    openModal,
    closeModal,
  } = useCampaignBuilder();

  // All of the bounds below are computed in the CAMPAIGN's zone, not the browser's, so the last
  // allowed day on screen matches the server's cap (activation/schedule dates are already wall-time
  // dates in this zone; only "today" and the 12-month max need converting).
  const timeZone = resolveCampaignTimeZone({ notificationMode, scheduleTz, activationTz });
  const todayInZone = isoToZonedSlot(new Date().toISOString(), timeZone).date;

  // Self-send modes (manual + universal) have no email send, so their expiry clock starts at the LINK
  // activation date rather than a send date — only the anchor wording + floor differ from gfg.
  const usesActivationAnchor = notificationMode === "manual" || notificationMode === "universal";

  // The anchor the window is measured from: for self-send links scheduled to go live later it's the
  // activation date (the recipient's clock starts then, not now); otherwise the scheduled send date,
  // else "now" for ASAP/immediate — the same anchor the server's expiry floor uses.
  const activationAnchor =
    usesActivationAnchor && activationMode === "scheduled" && activationDate ? activationDate : "";
  const sendAnchor =
    activationAnchor || (scheduleMode === "certain" && scheduleDate ? scheduleDate : todayInZone);

  // The earliest date the custom picker allows: after the send/activation date AND never in the past
  // — the same floor the server enforces. For a live campaign whose send is already past, this falls
  // back to tomorrow so expiry can't be set to a past date (which would close the campaign on the
  // next cron and irreversibly donate/roll over its unredeemed credits).
  const tomorrow = defaultScheduleDate();
  const expiryMin = sendAnchor > tomorrow ? sendAnchor : tomorrow;

  // The latest date allowed: 12 months out from today IN THE CAMPAIGN ZONE, because that's when the
  // credits funding the campaign expire. The native `max` caps the picker UI; handleCustomExpiryDate
  // clamps typed/pasted input, which max alone doesn't block in every browser.
  const expiryMax = addMonthsToDateStr(todayInZone, EXPIRY_MAX_MONTHS);

  // Whether the picker just auto-corrected the chosen date into [min, max] — prepends "Moved into
  // range." to the hint and tints it, so the sender notices the date they see isn't the one they set.
  const [movedIntoRange, setMovedIntoRange] = useState(false);
  // Reset the note when leaving custom so it never reads stale on a later return to the picker.
  useEffect(() => {
    if (expiryMode !== "custom") setMovedIntoRange(false);
  }, [expiryMode]);

  // Keep a chosen custom date inside [min, max] as those bounds move — the floor shifts when the
  // sender changes the activation date, which can leave an earlier expiry sitting before go-live.
  // Pull it forward rather than let a campaign close before it opens; clamp back from beyond 12mo too.
  useEffect(() => {
    if (expiryMode !== "custom" || !customExpiryDate) return;
    if (customExpiryDate < expiryMin) {
      setCustomExpiryDate(expiryMin);
      setMovedIntoRange(true);
    } else if (customExpiryDate > expiryMax) {
      setCustomExpiryDate(expiryMax);
      setMovedIntoRange(true);
    }
  }, [expiryMode, customExpiryDate, expiryMin, expiryMax, setCustomExpiryDate]);

  // Whether the floor is the activation date (manual, future go-live) rather than just "today" —
  // the hint says so, so the sender understands why the earliest allowed date isn't today.
  const flooredByActivation = !!activationAnchor && expiryMin === activationAnchor;
  const baseHint = flooredByActivation
    ? `Must fall between ${formatDisplayDate(expiryMin)} (when links activate) and ${formatDisplayDate(
        expiryMax,
      )} — campaigns run for a maximum of ${EXPIRY_MAX_MONTHS} months.`
    : `Campaigns run for a maximum of ${EXPIRY_MAX_MONTHS} months, matching the life of the credit funding them (latest: ${formatDisplayDate(
        expiryMax,
      )}).`;
  const maxHint = movedIntoRange ? `Moved into range. ${baseHint}` : baseHint;

  // A custom date under 7 days from send prompts a confirmation; Cancel reverts the pick. A typed
  // date beyond the 12-month cap is clamped before anything else looks at it (and flagged as moved).
  function handleCustomExpiryDate(raw: string) {
    const previous = customExpiryDate;
    const clampedMax = !!raw && raw > expiryMax;
    const next = clampedMax ? expiryMax : raw;
    setMovedIntoRange(clampedMax);
    setCustomExpiryDate(next);
    const days = expiryWindowDays(sendAnchor, next);
    if (days !== null && days >= 1 && days < SHORT_EXPIRY_DAYS) {
      const revert = () => {
        setCustomExpiryDate(previous);
        closeModal();
      };
      openModal({
        context: "shortExpiryWarning",
        shortExpiryDays: days,
        onPrimary: closeModal,
        onSecondary: revert,
        onDismiss: revert,
      });
    }
  }

  // The presets are identical in every mode — only the anchor phrase changes: GfG sends emails so the
  // clock starts at the send date; self-send (manual + universal) has no send, so it starts at the link
  // activation date. (expiry is mode-agnostic; only wording).
  const anchor = usesActivationAnchor ? "activation date" : "send date";
  const presetLabel = (mode: "30" | "60" | "90" | "6mo") =>
    mode === "6mo" ? `6 months from ${anchor}` : `${mode} days from ${anchor}`;
  const description = expiryMode === "custom" ? "Custom date" : presetLabel(expiryMode);

  return (
    <Accordion
      title="Expiration"
      description={description}
      icon={<EventBusyIcon sx={{ fontSize: 24 }} />}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-2 mt-4">
        <RadioOption
          selected={expiryMode === "30"}
          onSelect={() => setExpiryMode("30")}
          label={presetLabel("30")}
        />
        <RadioOption
          selected={expiryMode === "60"}
          onSelect={() => setExpiryMode("60")}
          label={presetLabel("60")}
          hint="The sweet spot — plenty of runway to claim a gift, without leaving budget open indefinitely."
          recommended
        />
        <RadioOption
          selected={expiryMode === "90"}
          onSelect={() => setExpiryMode("90")}
          label={presetLabel("90")}
        />
        <RadioOption
          selected={expiryMode === "6mo"}
          onSelect={() => setExpiryMode("6mo")}
          label={presetLabel("6mo")}
        />
        <RadioOption
          selected={expiryMode === "custom"}
          onSelect={() => setExpiryMode("custom")}
          label="Set a custom date"
          hint={
            usesActivationAnchor
              ? "Pick an exact date for all links to expire."
              : "Not available when using multiple send dates."
          }
          hasVisiblePanel={expiryMode === "custom"}
        />
        <ConditionalPanel visible={expiryMode === "custom"} merged>
          <p className="font-lato text-[12px] font-bold text-graphite mb-2">
            Custom Expiration Date
          </p>
          <StyledInput
            type="date"
            min={expiryMin}
            max={expiryMax}
            value={customExpiryDate}
            onChange={(e) => handleCustomExpiryDate(e.target.value)}
            style={{ maxWidth: 240 }}
          />
          <p
            className="font-lato text-[12px] mt-2 leading-relaxed"
            style={{
              color: movedIntoRange ? "var(--color-good-wine)" : "var(--color-graphite)",
              fontWeight: movedIntoRange ? 600 : undefined,
            }}
          >
            {maxHint}
          </p>
        </ConditionalPanel>
      </div>
    </Accordion>
  );
}
